import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { ImageLightbox } from '../components/ImageLightbox';
import { ReconnectDriveButton, useDriveConnection } from '../features/auth/ReconnectDrive';
import { canSee, isSuperAdmin, useMyPermissions } from '../features/auth/useMyPermissions';
import { SidebarGuides } from '../features/guide/SidebarGuides';
import { SidebarScene, TopbarDecor } from '../features/christmas/Christmas';
import { SnowCanvas } from '../features/christmas/SnowCanvas';
import type { ReactNode } from 'react';
import { Link, NavLink, Outlet, useNavigate, useOutletContext } from 'react-router';
import { useInbox } from '../features/notifications/hooks';
import { useHuddleChatUnread, useRoomUnread } from '../features/messages/hooks';
import { totalUnreadBadgeText } from '../features/messages/rooms';
import { withHuddleUnread } from '../features/messages/huddleChat';
import { useMessageAlerts } from '../features/messages/useMessageAlerts';
import { useSalesAccess } from '../features/tracker/hooks';
import { useCgAccess } from '../features/cgOrders/hooks';
import { MimicProvider } from '../features/supplierOrganizations/MimicMode';
import { HuddleShellPill } from '../features/huddles/HuddleBar';
import { unreadCount } from '../features/notifications/types';
import { useAuth } from '../features/auth/AuthProvider';
import type { Profile } from '../features/auth/access';
import { Avatar, Menu } from '../components/ui';
import { Icon, IconTile } from '../components/ui/Icon';
import type { IconName, IconTone } from '../components/ui/Icon';
import { applyTheme, currentTheme } from '../lib/theme';
import type { Theme } from '../lib/theme';
import {
  applyUiScale,
  currentScale,
  DEFAULT_UI_SCALE,
  percentOf,
  stepScale,
  UI_SCALES,
} from '../lib/uiScale';
import type { UiScale } from '../lib/uiScale';

function displayNameOf(profile: Profile): string {
  return profile.display_name.trim() !== '' ? profile.display_name : profile.email;
}

/**
 * The page title, when a page has asked for it to live in the top bar.
 *
 * The top bar's left half was empty on every desktop screen in the app while
 * a 54px title block plus 32px of margin sat below it repeating what the
 * sidebar had already highlighted. Moving the title up recovers 94px — two
 * more rows of a dense table — and costs no horizontal room at all.
 *
 * A context rather than a portal: a portal has to find its target node, which
 * does not exist on the child's first render, and the workarounds for that all
 * end in a frame where the title is missing. The shell owns the state and
 * renders it itself, so there is nothing to find.
 */
export interface TopbarTitle {
  title: string;
  /** A parent to return to, for a page that is one of many. */
  breadcrumb?: { label: string; to: string };
}

/**
 * null when there is no shell above this page.
 *
 * That case is real — tests mount a page on its own, and so would any future
 * embed — and the honest answer to "there is no top bar to put this in" is to
 * keep the title on the page rather than to drop it. A no-op setter here
 * would have looked equivalent and silently rendered pages with no heading at
 * all, which is what the first version of this did.
 */
const SetTopbarTitle = createContext<((value: TopbarTitle | null) => void) | null>(null);

/** So the main landmark can be named by the page title that left it. */
const TOPBAR_TITLE_ID = 'shell-page-title';

interface TopBarProps {
  profile: Profile;
  navOpen: boolean;
  navId: string;
  onToggleNav: () => void;
  toggleRef: React.RefObject<HTMLButtonElement | null>;
  pageTitle: TopbarTitle | null;
}

/**
 * The light/dark switch, top right. The choice is applied to <html> (one
 * attribute flips every design token), remembered in the browser, and each
 * device can hold its own preference.
 */
export function ThemeToggle() {
  const [theme, setTheme] = useState<Theme>(() => currentTheme());

  // A segmented control rather than a cycling button: with three options a
  // toggle makes you guess how many presses away your choice is, and the
  // middle option is the one people would otherwise never find.
  const OPTIONS: { value: Theme; glyph: string; label: string }[] = [
    { value: 'clay', glyph: '🪨', label: 'Clay' },
    { value: 'dusk', glyph: '🌆', label: 'Dusk' },
    { value: 'dark', glyph: '🌙', label: 'Dark' },
    { value: 'matcha', glyph: '🍵', label: 'Matcha' },
    { value: 'ocean', glyph: '🌊', label: 'Ocean' },
  ];

  return (
    <div className="shell-theme-switch" role="group" aria-label="Theme">
      {OPTIONS.map((option) => (
        <button
          key={option.value}
          type="button"
          className={`shell-theme-option${theme === option.value ? ' shell-theme-option--on' : ''}`}
          aria-pressed={theme === option.value}
          aria-label={option.label}
          title={option.label}
          onClick={() => {
            applyTheme(option.value);
            setTheme(option.value);
          }}
        >
          <span aria-hidden="true">{option.glyph}</span>
        </button>
      ))}
    </div>
  );
}

/**
 * Text size, beside the theme switch and in the same shape, because they are
 * the same kind of thing: two per-device display preferences that belong to
 * the person rather than to the page.
 *
 * Stepper rather than a menu of four. The point of the control is to try a
 * size, look at a real screen of your own data, and adjust — which is one
 * press per attempt with +/−, and two per attempt through a menu.
 *
 * The middle slot does double duty as the readout and the reset. It has to
 * show the current size for the control to be usable at all after a reload
 * (otherwise the only way to know where you are is to count how far the
 * buttons still move), and the brief asks for a way back to 100%; giving both
 * jobs to one slot is what keeps this to three buttons next to a four-button
 * group instead of five.
 */
export function TextSizeControl() {
  const [scale, setScale] = useState<UiScale>(() => currentScale());

  const change = (next: UiScale) => {
    applyUiScale(next);
    setScale(next);
  };

  const smallest = UI_SCALES[0];
  const largest = UI_SCALES[UI_SCALES.length - 1];
  const percent = percentOf(scale);

  return (
    <div className="shell-theme-switch shell-text-size" role="group" aria-label="Text size">
      <button
        type="button"
        className="shell-theme-option"
        onClick={() => {
          change(stepScale(scale, -1));
        }}
        disabled={scale === smallest}
        aria-label="Smaller text"
        title="Smaller text"
      >
        <span aria-hidden="true">A&minus;</span>
      </button>
      {/* Not disabled at 100%: it is the readout first, and a readout that
          greys out two thirds of the time reads as a fault rather than as a
          state. Pressing it at 100% re-applies 100%, which also writes the
          choice to storage — harmless, and mildly useful. */}
      <button
        type="button"
        className="shell-theme-option shell-text-size-value"
        onClick={() => {
          change(DEFAULT_UI_SCALE);
        }}
        aria-label={`Text size ${String(percent)} percent. Reset to 100 percent.`}
        title="Reset text size"
      >
        {percent}%
      </button>
      <button
        type="button"
        className="shell-theme-option"
        onClick={() => {
          change(stepScale(scale, 1));
        }}
        disabled={scale === largest}
        aria-label="Larger text"
        title="Larger text"
      >
        <span aria-hidden="true">A+</span>
      </button>
    </div>
  );
}

function TopBar({ profile, navOpen, navId, onToggleNav, toggleRef, pageTitle }: TopBarProps) {
  const { signOut, reconnectDrive } = useAuth();
  const navigate = useNavigate();
  const name = displayNameOf(profile);
  // Everyone uploads; only three people can reach the Admin page. Without
  // this, six of nine could only connect Drive by first hitting a failure.
  const drive = useDriveConnection();
  return (
    <header className="shell-topbar">
      <button
        ref={toggleRef}
        type="button"
        className="shell-menu-toggle"
        aria-label="Toggle navigation"
        aria-expanded={navOpen}
        aria-controls={navId}
        onClick={onToggleNav}
      >
        Menu
      </button>
      {pageTitle === null ? null : (
        /* The crumb sits BESIDE the heading, not inside it. Nested, it became
           part of the heading's accessible name — "Projects / Corporate
           Gifting Q4" — so the page's own name stopped being what the heading
           was called. A breadcrumb is navigation; the heading is the name. */
        <div className="shell-topbar-title">
          {pageTitle.breadcrumb === undefined ? null : (
            <nav className="shell-topbar-crumbs" aria-label="Breadcrumb">
              <Link className="shell-topbar-crumb" to={pageTitle.breadcrumb.to}>
                {pageTitle.breadcrumb.label}
              </Link>
              <span className="shell-topbar-crumb-sep" aria-hidden="true">
                /
              </span>
            </nav>
          )}
          <h1 id={TOPBAR_TITLE_ID} className="shell-topbar-title-text">
            {pageTitle.title}
          </h1>
        </div>
      )}
      <span className="shell-topbar-spacer">
        {profile.account_type === 'tap' ? <TopbarDecor /> : null}
      </span>
      <TextSizeControl />
      <ThemeToggle />
      <Menu
        triggerLabel={`Account menu for ${name}`}
        trigger={
          <>
            <Avatar name={name} imageUrl={profile.avatar_url} size="sm" />
            <span className="shell-user">{profile.email}</span>
          </>
        }
        items={[
          // TAP only. This same TopBar is what suppliers see, and /settings
          // sits behind RequireTap — an item that bounces you the moment you
          // press it is worse than no item.
          ...(profile.account_type === 'tap'
            ? [
                {
                  label: 'Settings',
                  onSelect: () => {
                    void navigate('/settings');
                  },
                },
              ]
            : []),
          {
            label: drive.data === true ? 'Reconnect Google Drive' : 'Connect Google Drive',
            onSelect: () => {
              void reconnectDrive();
            },
          },
          {
            label: 'Sign out',
            danger: true,
            onSelect: () => {
              void signOut();
            },
          },
        ]}
      />
    </header>
  );
}

/**
 * Drive is connected once per person, and until it is, every upload and every
 * pasted screenshot fails an hour into the session. Waiting for that failure
 * to teach people is the wrong order: the fix is one click, so ask for it
 * before the thing breaks rather than after.
 *
 * Suppliers are not asked — they upload through their own portal path and have
 * no Drive of ours to connect.
 */
function DriveConnectionBanner() {
  const connection = useDriveConnection();
  const [dismissed, setDismissed] = useState(false);

  if (dismissed || connection.data !== false) {
    return null;
  }
  return (
    <div className="drive-banner">
      <span>
        Connect Google Drive to upload files and paste screenshots. It takes one click and lasts.
      </span>
      <ReconnectDriveButton label="Connect Google Drive" />
      <button
        type="button"
        className="drive-banner-dismiss"
        onClick={() => setDismissed(true)}
        aria-label="Dismiss"
      >
        Not now
      </button>
    </div>
  );
}

function Shell({
  profile,
  brand,
  shellKind,
  navLabel,
  nav,
  banner,
}: {
  profile: Profile;
  brand: string;
  shellKind: 'tap' | 'supplier';
  navLabel: string;
  nav: ReactNode;
  banner?: ReactNode;
}) {
  const [navOpen, setNavOpen] = useState(false);
  // Set by whichever PageHeader is mounted, cleared when it unmounts, so
  // navigating away never leaves the previous page's name overhead.
  const [pageTitle, setPageTitle] = useState<TopbarTitle | null>(null);
  const navId = useId();
  const toggleRef = useRef<HTMLButtonElement | null>(null);

  const closeNav = useCallback((refocusToggle: boolean) => {
    setNavOpen(false);
    if (refocusToggle) {
      toggleRef.current?.focus();
    }
  }, []);

  // Mobile drawer behavior: Escape closes and restores focus to the Menu
  // button; background scrolling is prevented while the drawer is open.
  useEffect(() => {
    if (!navOpen) {
      return;
    }
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeNav(true);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [navOpen, closeNav]);

  return (
    <div className="shell" data-shell={shellKind}>
      {/* Christmas snow, TAP Hub only (the supplier portal and the TAP Console
          are untouched). Off out of season or when the person turns it off. */}
      {shellKind === 'tap' ? <SnowCanvas /> : null}
      {/* One listener for the whole app: every image in rich text becomes
          click-to-zoom without each render site opting in. */}
      <ImageLightbox />
      {navOpen ? (
        <button
          type="button"
          className="shell-backdrop"
          aria-label="Close navigation"
          onClick={() => {
            closeNav(true);
          }}
        />
      ) : null}
      <nav
        id={navId}
        className="shell-sidebar"
        aria-label={navLabel}
        data-open={navOpen}
        onClick={(event) => {
          // Selecting a navigation link closes the drawer.
          if (navOpen && (event.target as HTMLElement).closest('a')) {
            closeNav(true);
          }
        }}
      >
        <div className="shell-brand">{brand}</div>
        {nav}
        {shellKind === 'tap' ? <SidebarScene /> : null}
        {/* Pinned to the bottom by margin-top:auto rather than by being
            positioned, so it sits under the last nav item on a short sidebar
            and still reaches the floor on a tall one. */}
        <SidebarGuides shellKind={shellKind} />
      </nav>
      <div className="shell-main">
        <DriveConnectionBanner />
        {banner}
        <TopBar
          profile={profile}
          navOpen={navOpen}
          navId={navId}
          toggleRef={toggleRef}
          pageTitle={pageTitle}
          onToggleNav={() => {
            setNavOpen((value) => !value);
          }}
        />
        <SetTopbarTitle.Provider value={setPageTitle}>
          {/* The page's h1 now lives in the bar, outside this landmark, so
              somebody navigating straight to `main` would arrive somewhere
              unnamed. Pointing at the title puts the name back without
              rendering it twice. */}
          <main
            className="shell-content"
            aria-labelledby={pageTitle === null ? undefined : TOPBAR_TITLE_ID}
          >
            <Outlet context={profile} />
          </main>
        </SetTopbarTitle.Provider>
      </div>
    </div>
  );
}

const tapLink = 'shell-nav-link';

/**
 * The sidebar, in four groups.
 *
 * Grouping is the point: twelve flat links are a list you read every time,
 * four groups are a shape you learn once. The dividers carry that shape, so
 * they are drawn BETWEEN rendered groups rather than after each one — a rule
 * above nothing is a rule that says a group was hidden from you.
 */
interface NavItem {
  to: string;
  label: string;
  icon: IconName;
  iconTone: IconTone;
  /** The module this link belongs to, for permission filtering. */
  module?: string;
  /** An extra gate that must also pass, e.g. the sales membership list. */
  extra?: boolean;
  badge?: number;
  /**
   * A badge that is already text (Messages: "99+" above 99). Null or absent
   * means no badge; wins over `badge` when both are set.
   */
  badgeText?: string | null;
}

/** What the nav count shows, or null for nothing (hidden at 0). */
function navBadge(item: NavItem): string | null {
  if (item.badgeText !== undefined && item.badgeText !== null) {
    return item.badgeText;
  }
  return item.badge !== undefined && item.badge > 0 ? String(item.badge) : null;
}

type NavGroupTone = 'personal' | 'creative' | 'sales' | 'operations';

/** The mockup assigns identity per destination, independently of its group. */
export const TAP_NAV_ICONS = {
  Home: { icon: 'home', iconTone: 'blue' },
  'My Day': { icon: 'sun', iconTone: 'orange' },
  'My Notes': { icon: 'notes', iconTone: 'amber' },
  Inbox: { icon: 'inbox', iconTone: 'blue' },
  Messages: { icon: 'comment', iconTone: 'green' },
  Projects: { icon: 'projects', iconTone: 'purple' },
  'Social Studio': { icon: 'social', iconTone: 'pink' },
  'Nurture Studio': { icon: 'nurture', iconTone: 'green' },
  'Sales Tracker': { icon: 'tracker', iconTone: 'blue' },
  Deals: { icon: 'deals', iconTone: 'purple' },
  'Sales Tasks': { icon: 'tasks', iconTone: 'green' },
  'CG Orders': { icon: 'orders', iconTone: 'orange' },
  'CEO Brief': { icon: 'brief', iconTone: 'blue' },
  'Page Pulse': { icon: 'tracker', iconTone: 'pink' },
  'Marketing Scoreboard': { icon: 'tracker', iconTone: 'purple' },
  Leaves: { icon: 'calendar', iconTone: 'green' },
  Hiring: { icon: 'files', iconTone: 'blue' },
  'Supplier Reviews': { icon: 'reviews', iconTone: 'orange' },
  Files: { icon: 'files', iconTone: 'blue' },
  Admin: { icon: 'admin', iconTone: 'grey' },
} as const satisfies Record<string, { icon: IconName; iconTone: IconTone }>;

interface NavGroup {
  tone: NavGroupTone;
  items: NavItem[];
}

function NavGroups({ groups }: { groups: NavGroup[] }) {
  const rendered = groups.filter((group) => group.items.length > 0);
  return (
    <>
      {rendered.map((group, index) => (
        <div key={group.tone} className="shell-nav-group" data-nav-tone={group.tone}>
          {index > 0 ? <hr className="shell-nav-divider" /> : null}
          {group.items.map((item) => {
            const badge = navBadge(item);
            return (
              <NavLink
                key={item.to}
                className={badge !== null ? `${tapLink} shell-nav-link--attention` : tapLink}
                to={item.to}
              >
                <IconTile name={item.icon} tone={item.iconTone} className="shell-nav-icon" />
                <span className="shell-nav-text">{item.label}</span>
                {badge !== null ? <span className="shell-nav-count">{badge}</span> : null}
              </NavLink>
            );
          })}
        </div>
      ))}
    </>
  );
}

export function TapLayout() {
  const profile = useOutletContext<Profile>();
  // The Inbox already polls; the shell reuses that same cache entry, so
  // anything aimed at you (mention, assignment, review event) bolds the
  // menu the moment it lands.
  const inbox = useInbox();
  const unread = unreadCount(inbox.data ?? []);
  // Messages: the sum of every room's unread count (my_room_unread, 3b-1),
  // plus the Alex Huddle chat's when the person has one (3b-2,
  // my_huddle_chat_unread), bold like Inbox, hidden at 0, "99+" above 99.
  // Both RPCs only count what the caller may see, so nothing here decides
  // privacy.
  const roomUnread = useRoomUnread();
  const huddleUnread = useHuddleChatUnread();
  const messagesBadge = totalUnreadBadgeText(withHuddleUnread(roomUnread.data, huddleUnread.data));
  // Desktop alerts: "(N) <title>" from this same badge, and a pop-up plus a
  // soft sound for a new message while the tab is in the background.
  useMessageAlerts({ badge: messagesBadge, roomUnread: roomUnread.data, inbox: inbox.data });
  const isAdmin = profile.internal_role === 'tap_admin';
  // The database's sales gate decides; the menu link just mirrors it.
  const salesAccess = useSalesAccess();
  const cgAccess = useCgAccess();
  const permissions = useMyPermissions();

  // Both gates must pass. The matrix governs navigation today, but the older
  // membership lists still govern the data, and a link to a page that will
  // refuse you is worse than no link.
  const visible = (items: NavItem[]) =>
    items.filter(
      (item) =>
        (item.extra ?? true) &&
        (item.module === undefined || canSee(permissions.data, item.module)),
    );

  const groups: NavGroup[] = [
    {
      tone: 'personal',
      items: visible([
        // My Tasks left this list on Aug 17: it is not a destination the way
        // Projects or Files are, it is one of the things you check when you sit
        // down — which is what Home already is. It is now a sub-tab at
        // /home/tasks, and the old /my-tasks URL redirects there.
        { to: '/home', label: 'Home', ...TAP_NAV_ICONS.Home },
        // My Day took Personal Tasks' place on 5 Sep: same rows, arranged for
        // the day. /personal still answers, unlinked, for now.
        { to: '/my-day', label: 'My Day', ...TAP_NAV_ICONS['My Day'] },
        // Its own item rather than a Home sub-tab: notes are opened directly and
        // repeatedly, not as part of a morning glance.
        { to: '/notes', label: 'My Notes', ...TAP_NAV_ICONS['My Notes'] },
        { to: '/inbox', label: 'Inbox', badge: unread, ...TAP_NAV_ICONS.Inbox },
        {
          to: '/messages',
          label: 'Messages',
          badgeText: messagesBadge,
          ...TAP_NAV_ICONS.Messages,
        },
      ]),
    },
    {
      tone: 'creative',
      items: visible([
        {
          to: '/projects',
          label: 'Projects',
          module: 'projects',
          ...TAP_NAV_ICONS.Projects,
        },
        {
          to: '/social-studio',
          label: 'Social Studio',
          module: 'social_studio',
          ...TAP_NAV_ICONS['Social Studio'],
        },
      ]),
    },
    {
      tone: 'sales',
      items: visible([
        {
          to: '/studio',
          label: 'Nurture Studio',
          module: 'nurture_studio',
          ...TAP_NAV_ICONS['Nurture Studio'],
        },
        {
          // Called "Outbound" from the migration until Aug 26, when the page it
          // opens, the permissions matrix and the handbook all said Sales
          // Tracker and only this line did not. A menu item that does not match
          // the heading it lands on makes people ask whether they are in the
          // right place, and it makes a request for help ambiguous.
          //
          // Note this is NOT the same word as the Inbound/Outbound direction on
          // a lead. That vocabulary is untouched and must stay: it names which
          // way the first contact went, not which module you are in.
          to: '/tracker',
          label: 'Sales Tracker',
          ...TAP_NAV_ICONS['Sales Tracker'],
          module: 'sales_tracker',
          extra: salesAccess.data === true,
        },
        {
          // Its own page since Aug 14: reaching Deals via the Sales Tracker then
          // a tab was two steps for something people open directly, and the URL
          // could not be shared. Gated on its own matrix module so access can be
          // narrowed independently of the rest of the tracker later.
          to: '/deals',
          label: 'Deals',
          ...TAP_NAV_ICONS.Deals,
          module: 'deals',
          extra: salesAccess.data === true,
        },
        {
          // Directly under Deals, by Kyu's instruction on 14 September. Its own
          // item rather than a tab inside the tracker because it holds work
          // from three modules — the tracker's own tasks, deal tasks and
          // prospect tasks — and a tab inside one of them would be the page
          // living under a third of what it shows.
          to: '/sales-tasks',
          label: 'Sales Tasks',
          ...TAP_NAV_ICONS['Sales Tasks'],
          module: 'sales_tracker',
          extra: salesAccess.data === true,
        },
        {
          to: '/cg-orders',
          label: 'CG Orders',
          ...TAP_NAV_ICONS['CG Orders'],
          module: 'cg_orders',
          extra: cgAccess.data === true,
        },
        {
          // No module gate. The page gates itself on is_super_admin(), which no
          // matrix module expresses, and the table refuses everybody else
          // regardless. `extra` is the whole gate here.
          to: '/ceo-brief',
          label: 'CEO Brief',
          ...TAP_NAV_ICONS['CEO Brief'],
          extra: isSuperAdmin(permissions.data),
        },
        {
          // No module gate and no extra: every TAP user may read it (D10);
          // read_page_pulse_team refuses anybody else.
          to: '/page-pulse',
          label: 'Page Pulse',
          ...TAP_NAV_ICONS['Page Pulse'],
        },
        {
          // Step 1 is TAP-readable, owner/admin editable. The RPC decides
          // per-section can_edit. No Step 3 analysis grant is implied here.
          to: '/marketing-scoreboard',
          label: 'Marketing Scoreboard',
          ...TAP_NAV_ICONS['Marketing Scoreboard'],
        },
      ]),
    },
    {
      tone: 'operations',
      items: visible([
        {
          // No module gate, deliberately. Every other item here is a place only
          // some people work; this is the one everybody needs, because everybody
          // takes leave. The database says the same thing — the row policy is
          // is_tap_user(), not a membership list — so gating the link would hide
          // a page the person is allowed to open.
          to: '/leaves',
          label: 'Leaves',
          ...TAP_NAV_ICONS.Leaves,
        },
        { to: '/hiring', label: 'Hiring', ...TAP_NAV_ICONS.Hiring },
        {
          to: '/supplier-reviews',
          label: 'Supplier Reviews',
          ...TAP_NAV_ICONS['Supplier Reviews'],
          module: 'supplier_reviews',
        },
        { to: '/files', label: 'Files', module: 'files', ...TAP_NAV_ICONS.Files },
        {
          to: '/admin',
          label: 'Admin',
          module: 'admin',
          extra: isAdmin,
          ...TAP_NAV_ICONS.Admin,
        },
      ]),
    },
  ];

  return (
    <Shell
      profile={profile}
      brand="TAP Hub"
      banner={<HuddleShellPill />}
      shellKind="tap"
      navLabel="TAP navigation"
      nav={<NavGroups groups={groups} />}
    />
  );
}

export function SupplierLayout() {
  const profile = useOutletContext<Profile>();
  return (
    <MimicProvider>
      <Shell
        profile={profile}
        brand="4ocean Supplier"
        shellKind="supplier"
        navLabel="Supplier navigation"
        nav={
          <NavLink className={tapLink} to="/supplier" end>
            <Icon name="home" />
            <span className="shell-nav-text">Portal</span>
          </NavLink>
        }
      />
    </MimicProvider>
  );
}

/**
 * The page's name and, sometimes, a sentence under it.
 *
 * `topbar` moves the name into the shell's top bar and retires the 86px block
 * this used to draw. It is opt-in per route rather than a blanket move,
 * because the trade is only worth making where the page is dense and visited
 * daily — on Settings or Admin there is no row of data waiting for the space,
 * and a title in its normal place is easier to land on.
 *
 * A description survives the move whenever it says something the title cannot.
 * "Leads, pipeline and outreach" under a heading called Sales Tracker is a
 * restatement and goes; "Nobody else can see anything here" is a promise about
 * privacy, and "Sample reviews live on the order's own page" is a signpost, so
 * both stay on the page as a lede under the moved title.
 */
export function PageHeader({
  title,
  description,
  topbar = false,
  breadcrumb,
}: {
  title: string;
  description?: string;
  /** Put the name in the shell's top bar instead of on the page. */
  topbar?: boolean;
  /** Shown before the name, for a page that is one of many. */
  breadcrumb?: { label: string; to: string };
}) {
  const setTopbarTitle = useContext(SetTopbarTitle);
  // No shell above us means no bar to move into, so the title stays put.
  const moves = topbar && setTopbarTitle !== null;
  const crumbLabel = breadcrumb?.label;
  const crumbTo = breadcrumb?.to;
  // Layout effect, not effect: this runs before paint, so the bar never shows
  // a frame without the title. Depends on the crumb's PARTS rather than the
  // object, which is rebuilt on every render and would loop.
  useLayoutEffect(() => {
    if (!moves || setTopbarTitle === null) return undefined;
    setTopbarTitle({
      title,
      ...(crumbLabel !== undefined && crumbTo !== undefined
        ? { breadcrumb: { label: crumbLabel, to: crumbTo } }
        : {}),
    });
    return () => {
      setTopbarTitle(null);
    };
  }, [moves, title, crumbLabel, crumbTo, setTopbarTitle]);

  if (moves) {
    return description ? <p className="page-lede">{description}</p> : null;
  }
  return (
    <div className="page-header">
      <h1>{title}</h1>
      {description ? <p>{description}</p> : null}
    </div>
  );
}
